const METRIC_LABELS = {
  searches30d: '搜索量',
  purchaseRate: '购买转化率',
  competition: '竞争度',
  relevanceScore: '相关性',
  clickThroughRate: '点击率',
  attributedConversionRate24h: 'AC 转化率',
  cpc: 'CPC',
};

const PPC_LABELS = {
  new_product: '新品期',
  stabilization: '稳定期',
  mature: '成熟期',
  deferred: '暂缓',
  blocked: '禁止',
};

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function count(value) {
  return asArray(value).length;
}

export function buildKeywordStrategyViewModel(strategy = {}) {
  const metricAvailability = Object.entries(strategy?.metricProfiles || {}).map(([metric, profile]) => ({
    metric,
    label: METRIC_LABELS[metric] || metric,
    observed: profile?.status === 'observed',
    observedCount: Number(profile?.observedCount || 0),
  }));
  const ppcCounts = Object.entries(strategy?.ppcKeywordPlan || {}).map(([stage, terms]) => ({
    stage,
    label: PPC_LABELS[stage] || stage,
    count: count(terms),
  }));
  const allocation = strategy?.listingAllocation || {};
  return {
    total: Number(strategy?.summary?.totalKeywords || count(strategy?.terms)),
    rootCount: Number(strategy?.summary?.roots || count(strategy?.rootLibrary)),
    primaryCount: Number(strategy?.summary?.primaryKeywords || count(strategy?.primaryKeywords)),
    negativeCount: Number(strategy?.summary?.negativeKeywords || count(strategy?.negativeLibrary)),
    deferredPpcCount: Number(strategy?.summary?.deferredPpcKeywords || 0),
    metricAvailability,
    ppcCounts,
    allocation: [
      ['前台核心', count(allocation.frontendCore)],
      ['前台辅助', count(allocation.frontendSupport)],
      ['后台候选', count(allocation.backendCandidates)],
      ['仅 PPC', count(allocation.ppcOnly)],
      ['拦截/否定', count(allocation.blocked)],
    ],
  };
}

function node(tag, className = '', text = '') {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

function metricChip(item) {
  const chip = node('span', `keyword-strategy-chip${item.observed ? '' : ' muted'}`);
  chip.textContent = item.observed ? `${item.label} ${item.observedCount} 条` : `${item.label} 未提供`;
  return chip;
}

/** Render only an audit summary. Product Truth and semantic classification remain in the pipeline gates. */
export function renderKeywordStrategyView(container, strategy = null) {
  if (!container) return;
  if (!strategy || typeof strategy !== 'object') {
    container.hidden = true;
    container.replaceChildren();
    return;
  }
  const model = buildKeywordStrategyViewModel(strategy);
  const shell = node('section', 'keyword-strategy-shell');
  const head = node('div', 'keyword-strategy-head');
  const copy = node('div');
  copy.append(
    node('div', 'keyword-strategy-kicker', 'STEP 02 · 词库策略层'),
    node('div', 'keyword-strategy-title', '词根、分级、否定与 PPC 已分开管理'),
    node('p', 'keyword-strategy-copy', '指标只在数据源真实提供时参与分档；缺失值不会被当作低竞争或低 CPC。分类标签是语义复核候选，产品事实、侵权与最终 Search Terms 仍由后续闸门决定。'),
  );
  const refresh = node('button', 'ghost-button keyword-strategy-refresh', '刷新策略');
  refresh.type = 'button';
  refresh.id = 'refresh-keyword-strategy-button';
  head.append(copy, refresh);

  const counts = node('div', 'keyword-strategy-counts');
  for (const [label, value] of [
    ['总词', model.total], ['词根', model.rootCount], ['主推', model.primaryCount], ['否定', model.negativeCount], ['PPC 暂缓', model.deferredPpcCount],
  ]) {
    const chip = node('span', 'keyword-strategy-chip');
    chip.append(node('strong', '', String(value)), document.createTextNode(` ${label}`));
    counts.append(chip);
  }

  const details = node('div', 'keyword-strategy-detail-grid');
  const metrics = node('div', 'keyword-strategy-detail');
  metrics.append(node('div', 'keyword-strategy-label', '可用于分档的真实指标'), node('div', 'keyword-strategy-chip-row'));
  metrics.lastElementChild.append(...model.metricAvailability.map(metricChip));
  const ppc = node('div', 'keyword-strategy-detail');
  ppc.append(node('div', 'keyword-strategy-label', 'PPC 推广阶段'), node('div', 'keyword-strategy-chip-row'));
  ppc.lastElementChild.append(...model.ppcCounts.map((item) => {
    const chip = node('span', 'keyword-strategy-chip', `${item.label} ${item.count}`);
    return chip;
  }));
  const listing = node('div', 'keyword-strategy-detail');
  listing.append(node('div', 'keyword-strategy-label', 'Listing 分配'), node('div', 'keyword-strategy-chip-row'));
  listing.lastElementChild.append(...model.allocation.map(([label, itemCount]) => node('span', 'keyword-strategy-chip', `${label} ${itemCount}`)));
  details.append(metrics, ppc, listing);
  shell.append(head, counts, details);
  container.hidden = false;
  container.replaceChildren(shell);
}
